// Continuing a tasting on another device (docs/design/social-plan.md, "Continuing a tasting"): an
// opt-in, off by default. Off, nothing about a tasting leaves this device and no call is made. On,
// a signed-in person's unfinished tastings are carried to the edge, encrypted there, at each step
// (a wine's page changing, a wine saved, another wine in front) and when the app goes out of view,
// and removed when the tasting ends. There is no timer: a step is what triggers a write, writes go
// through the social outbox (so they survive being offline and one waiting write per tasting is
// all the queue ever holds), and one write per tasting is in flight at a time with a changed copy
// during it simply following.
import { useSyncExternalStore } from "react";
import { z } from "zod";
import { EdgeApiError } from "../edgeApi";
import { getAuthState, onSessionChange } from "../social/auth";
import { perform } from "../social/outbox";
import { getDraftSettings, lastCarried, patchDraftSettings } from "./draftApi";
import { isUnfinished, stepMark } from "./run";
import { getRuns, subscribeRuns } from "./store";

const KEY = "proef.draftSync";

/** The switch as this device last heard it, for the person it was heard for. */
const Mirror = z.object({ userId: z.string().min(1), enabled: z.boolean() });
type Mirror = z.infer<typeof Mirror>;

function readMirror(): Mirror | undefined {
  try {
    const parsed = Mirror.safeParse(JSON.parse(window.localStorage.getItem(KEY) ?? "null"));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

let mirror: Mirror | undefined = typeof window === "undefined" ? undefined : readMirror();
const listeners = new Set<() => void>();

function writeMirror(next: Mirror | undefined): void {
  mirror = next;
  try {
    if (next === undefined) window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Storage blocked: the switch is remembered for this page only.
  }
  listeners.forEach((listener) => {
    listener();
  });
}

function signedInId(): string | undefined {
  const auth = getAuthState();
  return auth.status === "signedIn" ? auth.user.id : undefined;
}

/** Whether keeping tastings for other devices is on for whoever is signed in; false until known. */
export function draftSyncEnabled(): boolean {
  const user = signedInId();
  return user !== undefined && mirror?.userId === user && mirror.enabled;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  const stop = onSessionChange(listener);
  return () => {
    listeners.delete(listener);
    stop();
  };
}

/** The switch for the settings drawer: undefined while it is not known (signed out, or never asked). */
export function useDraftSyncEnabled(): boolean | undefined {
  return useSyncExternalStore(
    subscribe,
    () => {
      const user = signedInId();
      return user !== undefined && mirror?.userId === user ? mirror.enabled : undefined;
    },
    () => undefined,
  );
}

/** What each tasting looked like when it was last asked to be carried, so only a step re-sends it. */
let marks = new Map<string, string>();
/** The tastings seen on the last pass, so one that ended is noticed. */
let known = new Set<string>();
const inFlight = new Set<string>();
const pending = new Set<string>();

function forgetAll(): void {
  marks = new Map();
  known = new Set();
  pending.clear();
}

/** A write that failed for a reason retrying cannot fix is dropped: the next step tries afresh. */
function ignore(error: unknown): void {
  if (error instanceof EdgeApiError && error.status === 403) {
    const user = signedInId();
    // The edge says the switch is off (turned off on another device): follow it.
    if (user !== undefined) writeMirror({ userId: user, enabled: false });
  }
}

function send(user: string, kind: "draft_put" | "draft_delete", runId: string): void {
  if (kind === "draft_put" && inFlight.has(runId)) {
    pending.add(runId);
    return;
  }
  inFlight.add(runId);
  perform(user, { kind, runId })
    .catch(ignore)
    .finally(() => {
      inFlight.delete(runId);
      if (pending.delete(runId) && draftSyncEnabled()) send(user, "draft_put", runId);
    });
}

/**
 * Looks at the tastings on this device and carries what changed in step: a tasting whose page,
 * wine or sent notes differ from the last pass is written, one that ended is removed. `everything`
 * writes each unfinished tasting whose content changed since it was last carried (the app going
 * out of view), and a tasting never carried before.
 */
export function reconcile(everything = false): void {
  const user = signedInId();
  if (user === undefined || !draftSyncEnabled()) {
    forgetAll();
    return;
  }
  const open = getRuns().filter(isUnfinished);
  const ids = new Set(open.map((run) => run.id));
  for (const gone of known) if (!ids.has(gone)) send(user, "draft_delete", gone);
  known = ids;
  for (const run of open) {
    const mark = stepMark(run);
    const stepped = marks.get(run.id) !== mark;
    const changed = lastCarried.get(run.id) !== run.updatedAt;
    marks.set(run.id, mark);
    if (changed && (stepped || everything)) send(user, "draft_put", run.id);
  }
}

/** Asks the edge whether the signed-in person turned the switch on, e.g. on another device. */
export async function refreshDraftSettings(): Promise<void> {
  const user = signedInId();
  if (user === undefined) return;
  try {
    const settings = await getDraftSettings();
    writeMirror({ userId: user, enabled: settings.enabled });
  } catch {
    // Offline or the edge down: the last answer stands.
  }
}

/**
 * The switch. Turning it on starts carrying every unfinished tasting; turning it off makes the edge
 * remove all of them (one call, in the same step as the consent), and nothing is written after.
 */
export async function setDraftSync(enabled: boolean): Promise<void> {
  const user = signedInId();
  if (user === undefined) return;
  const settings = await patchDraftSettings(enabled);
  forgetAll();
  writeMirror({ userId: user, enabled: settings.enabled });
  if (settings.enabled) reconcile(true);
}

/** Wires the sync to the tastings, the person and the app's visibility; returns the stop. */
export function startDraftSync(): () => void {
  const onRuns = (): void => {
    reconcile();
  };
  const onPerson = (): void => {
    if (signedInId() === undefined) {
      forgetAll();
      return;
    }
    void refreshDraftSettings().then(() => {
      reconcile();
      return undefined;
    });
  };
  const onHide = (): void => {
    if (document.visibilityState === "hidden") reconcile(true);
  };
  const onBack = (): void => {
    reconcile();
  };
  const stopRuns = subscribeRuns(onRuns);
  const stopPerson = onSessionChange(onPerson);
  document.addEventListener("visibilitychange", onHide);
  window.addEventListener("pagehide", onHide);
  window.addEventListener("online", onBack);
  onPerson();
  return () => {
    stopRuns();
    stopPerson();
    document.removeEventListener("visibilitychange", onHide);
    window.removeEventListener("pagehide", onHide);
    window.removeEventListener("online", onBack);
  };
}

/** Tests start from a switch nobody has answered. */
export function resetDraftSync(): void {
  forgetAll();
  inFlight.clear();
  mirror = readMirror();
}
